/**
 * The domain model, with no I/O and no dependency on how any of it is stored.
 *
 * Persisted shapes use ISO strings rather than `Date`, because these objects go
 * into DynamoDB and come back out of JSON. A `Date` that survives a write and
 * returns as a string is the kind of type lie that typechecks all the way to
 * production.
 */
/**
 * The name of a language, for telling a model which one to write in.
 *
 * **A locale code is not an instruction.** Every generation prompt used to say "write
 * in the same language as the objective", which asks the model to infer the language
 * from data it has been told to treat as untrusted — and inference is exactly what
 * drifts. A curriculum for an English objective, built from documents whose own front
 * matter declared `language="en"`, came back entirely in German. Nothing was
 * misconfigured; the instruction was simply relative where it needed to be absolute.
 *
 * So prompts name the language. This is the only place that mapping lives, because
 * three pipelines need the same answer and three copies of it would drift apart.
 */
export const LANGUAGE_NAMES = {
    en: 'English',
    es: 'Spanish (Latin American)',
};
export function languageName(locale) {
    return LANGUAGE_NAMES[locale];
}
/**
 * A smoke test for "did the model write in the language it was told to".
 *
 * Not language detection, and not trying to be: it looks for a handful of words that
 * are near-unavoidable in running prose in the expected language, and complains only
 * when *none* of them appear across a whole curriculum's worth of text. That is a
 * deliberately low bar — it cannot tell good Spanish from bad, and it will not fire on
 * a few borrowed terms, which is right, because technical material is full of them.
 *
 * It exists because the failure it catches is expensive and quiet. A learner
 * memorises a curriculum; one silently generated in the wrong language wastes the
 * research it was built from and every item generated afterwards. Failing the pass
 * costs one cheap re-run.
 */
const LANGUAGE_MARKERS = {
    en: ['the', 'and', 'of', 'for', 'with', 'that', 'from', 'which'],
    es: ['el', 'la', 'los', 'las', 'de', 'que', 'para', 'con', 'del', 'y'],
};
export function looksLikeLanguage(text, locale) {
    // Short text carries no signal — a handful of titles legitimately contains no
    // function words at all, and failing on that would be worse than not checking.
    const words = text.toLowerCase().match(/[\p{L}]+/gu) ?? [];
    if (words.length < 40)
        return true;
    const markers = new Set(LANGUAGE_MARKERS[locale]);
    return words.some((word) => markers.has(word));
}
// ---- Tiers, and what each one is entitled to ----
/**
 * The three tiers, and they are a closed list.
 *
 * Numbers rather than names because the product prices them as one, two and three objectives
 * and the names would be a second vocabulary to keep in step with the prices. `docs/objectives-design.md`
 * is where the reasoning lives; it is settled.
 *
 * **99 is not a fourth tier and is not for sale.** It exists because the grant is real
 * enforcement against a product with no billing behind it yet, and the only person using this
 * system is the one building it: tier 1's single monthly run refuses the second "look again" of
 * the day, so testing research means fighting a cap that is protecting no revenue. 99 is a
 * deliberate hole with a lock on it, and the lock is that nothing writes it — there is no
 * upgrade path, no admin button and no default that reaches it, so it can only arrive by
 * somebody typing it at a terminal against a named learner.
 *
 * **It ends when billing begins.** At that point the cap protects something real, and an
 * account that can spend unbounded research is a liability rather than a convenience.
 */
export const TIERS = [1, 2, 3, 99];
/** The tier that is not a tier. Exported so the two places that must name it cannot drift. */
export const INTERNAL_TIER = 99;
/**
 * The table, written out as literals rather than derived from the tier number.
 *
 * `activeObjectives: tier` and `researchRunsPerMonth: tier` would be true today and would
 * quietly answer for a tier 4 nobody priced. A record over a closed list cannot.
 */
const ENTITLEMENTS = {
    1: { activeObjectives: 1, researchRunsPerMonth: 1, research: 'basic' },
    2: { activeObjectives: 2, researchRunsPerMonth: 2, research: 'advanced' },
    3: { activeObjectives: 3, researchRunsPerMonth: 3, research: 'advanced' },
    /*
     * The internal tier. Research is effectively unbounded; **the slot cap is deliberately not.**
     *
     * One objective, the same as tier 1, and that is the point rather than an oversight. The
     * grant is the thing obstructing testing today, and lifting it costs nothing. The *slot* cap
     * is the last thing standing between a single-objective system and a multi-objective one
     * that is not finished: a second objective here would not fail, it would show the wrong one
     * in whatever is still singular, which is worse than a refusal.
     *
     * **Step 6 has landed and this is still 1, deliberately.** The readers it named are done —
     * nothing answers for "the" objective any more; progress, the sheet and the chat take a
     * plan id and share one rule for the default, the slot cap is read from here rather than
     * assumed, and the console shows every plan. What is not done is step 7 (priority weighting)
     * and step 8, and step 8 is the one that binds: `lastSeenMessageId` is still **one marker
     * per learner**, so a second plan's unread count is the first plan's, and there are no tabs
     * to choose a plan from — a learner would be able to reach the second plan only through a
     * notification's `?o=`. Raising the cap before then ships a product somebody cannot
     * navigate, not a broken one.
     *
     * Raise it when the tabs land, not before, and raise it here rather than by writing a
     * different tier onto a learner.
     *
     * A thousand rather than `Infinity`: the number is compared, logged and rendered, and a
     * rendered `Infinity` on a screen that says "runs left" is a bug report. A thousand runs is
     * $6,130 of research, which is not a budget — it is a number nobody reaches by testing and
     * everybody notices in the ledger if something goes wrong.
     */
    99: { activeObjectives: 1, researchRunsPerMonth: 1000, research: 'advanced' },
};
/**
 * What a row with no `tier` on it means, and what an unreadable one falls back to.
 *
 * **Read from the environment, so testing and production differ by a stack value rather than by
 * a commit.** `NABU_DEFAULT_TIER` is set to 99 today because the owner is testing with fresh
 * sign-ups and a grant of one research run a month makes the product untestable; unset, or set to
 * anything this file does not recognise, it is 1.
 *
 * **The first version of this made `DEFAULT_TIER` *be* `INTERNAL_TIER`, and that was wrong in a
 * way worth recording.** It collapsed two constants that mean opposite things — "what everybody
 * gets" and "the tier nobody may reach by accident" — and once they were the same value, no test
 * and no runbook sentence could tell them apart. A test asserting that `'99'` and `100` do *not*
 * reach the internal tier still passed, because they resolved to the default, which was now the
 * internal tier. It asserted nothing and read as though it asserted everything.
 *
 * **`tierOf` still fails downwards**, and that is the property being protected here: an
 * unparseable environment value is 1, not 99, so a typo in a stack cannot hand every account a
 * thousand research runs. The direction only inverts for a value somebody deliberately set.
 *
 * Setting this back to production behaviour is a stack change and a deploy — no code moves.
 */
export const DEFAULT_TIER = tierOfEnvironment(environmentTier());
/**
 * `NABU_DEFAULT_TIER`, or nothing at all in a browser.
 *
 * **`@nabu/shared` is imported by the web application**, and `process` does not exist there.
 * Reading it unguarded is a `ReferenceError` at module load — a white screen from a build that
 * exits 0, because `vite.config.ts` defines no `process` shim. It survives today only because
 * Rollup tree-shakes a `const` nothing imports, which is an accident rather than a guarantee:
 * `Curriculum.tsx` already imports from this module, and the first component to reach for
 * `tierOf` or `entitlement` drags the read into the bundle with it.
 *
 * So the guard is structural rather than incidental. In a browser the answer is `undefined`,
 * which `tierOfEnvironment` reads as tier 1 — the correct answer for a place that cannot be
 * told otherwise and never decides a refusal anyway.
 */
function environmentTier() {
    return typeof process === 'undefined' ? undefined : process.env['NABU_DEFAULT_TIER'];
}
/**
 * The tier a stored value names, or the default.
 *
 * **Falls back rather than throwing, and never reads a value it does not recognise.** A row
 * carrying `4`, `"2"`, `null` or nothing at all reads as `DEFAULT_TIER`, because the alternatives
 * are both worse: throwing turns a malformed row into a 500 on every screen the learner opens,
 * and taking the raw value lets a hand-edited console write name a tier nobody wrote a table row
 * for.
 *
 * **Where that fallback lands is the deployment's choice, not this function's.** It was always
 * tier 1 — the cheapest — on the argument that a learner wrongly on tier 1 complains while one
 * wrongly on tier 3 costs $18 and says nothing. That argument is intact and dormant: while
 * `NABU_DEFAULT_TIER` is 99 for testing, the fallback is the *most* generous entitlement, which
 * is safe only because nobody is billed. It returns to tier 1 with billing, by a stack change.
 */
export function tierOf(value) {
    return TIERS.includes(value) ? value : DEFAULT_TIER;
}
/**
 * The tier a stack's environment names, or 1.
 *
 * Separate from `tierOf` because it cannot use it: `tierOf` falls back to `DEFAULT_TIER`, which
 * is what this function is computing. A recursive default is not a default.
 *
 * **Matched as an exact decimal integer rather than passed to `Number`**, and the first version
 * of this did use `Number`, which was wrong in a way its own docblock got wrong too: `Number`
 * trims whitespace and reads hexadecimal, so `'99 '`, `' 99'`, `'99.0'` and — worst — `'0x63'`
 * all resolve to 99. The claim being made here is that a typo in a stack cannot hand every
 * account a thousand research runs, and four spellings of the internal tier that nobody typed on
 * purpose would have made that claim false.
 *
 * Falls to **1**, not to whatever was intended. An environment variable is a string typed into a
 * CDK file, so `'99 '`, `'ninetynine'` and a variable somebody removed all arrive here, and the
 * cheapest entitlement is the only safe answer for a value nobody can vouch for.
 *
 * Exported for the test that pins those spellings. Nothing else should call it — `DEFAULT_TIER`
 * is the answer it computes.
 */
export function tierOfEnvironment(raw) {
    if (raw === undefined || !/^[0-9]+$/.test(raw))
        return 1;
    const parsed = Number.parseInt(raw, 10);
    return TIERS.includes(parsed) ? parsed : 1;
}
/**
 * **The only place a number lives.** Every refusal reads it; nothing else knows a limit.
 *
 * That is the point of it being one function rather than three constants: the create path, the
 * research paths and the screen that tells a learner what they may do all have to agree, and
 * three copies of "one objective" is how the screen keeps offering a button the API refuses.
 *
 * Takes `unknown` rather than `Tier` deliberately. Every real caller is holding an attribute off
 * a DynamoDB row, and a cast at each call site is an invitation to cast the wrong thing — see
 * `tierOf` for what an unrecognised value answers and why it answers downwards.
 *
 * **Grants do not roll over.** An unspent run is gone when the month turns, which is what makes
 * the worst month exactly the month the tier was priced for. Where "month" begins is
 * `keys.researchGrant`'s business, and it is a placeholder until there is a billing anchor.
 */
export function entitlement(tier) {
    return ENTITLEMENTS[tierOf(tier)];
}
/**
 * What an objective that never said gets.
 *
 * Research, because that is what every objective did before the choice existed and because it
 * is the answer that cannot lose material: an objective wrongly treated as uploads-only sits
 * waiting for pages nobody is going to photograph, and waits silently.
 */
export const DEFAULT_PLAN_SOURCE = 'research';
export function isPlanSource(value) {
    return value === 'research' || value === 'uploads' || value === 'scoped';
}
/**
 * Which source a stored objective row declares.
 *
 * **One reader, because six call sites would be six chances to get the default backwards.**
 * The pipelines that consult this are spread across three packages — the API refusing a
 * research pass, `research.ts` refusing to spend, `extract.ts` deciding an upload is the thing
 * that builds the first plan — and they all take a raw DynamoDB row rather than a typed
 * `Objective`. A row is also the shape an unrecognised value arrives in, and this is where it
 * is turned back into the default rather than trusted into a `switch`.
 */
export function planSourceOf(row) {
    const declared = row?.['planFrom'];
    return isPlanSource(declared) ? declared : DEFAULT_PLAN_SOURCE;
}
/**
 * Whether this objective ever reaches a research model, by any route.
 *
 * Kept as its own question because several callers only want the coarse answer — may anything
 * at all be spent here — and spelling it `!== 'uploads'` at each of them is how the two halves
 * of F-118's refusal drift apart. Where the answer depends on *which* pass, `allowsResearch` is
 * the one to ask; this one is now too coarse to be a gate on spending, and F-119 moved every
 * gate that was using it as one.
 */
export function researches(source) {
    switch (source) {
        case 'research':
            return true;
        case 'uploads':
            return false;
        case 'scoped':
            return true;
    }
}
/**
 * Whether this objective may run this particular research pass.
 *
 * **An exhaustive `switch` on purpose, and that is the mechanism F-118 asked for rather than a
 * comment.** Its entry says a third `PlanSource` member must not be declarable without every
 * branch being revisited; a `switch` with no `default`, over a union whose every member returns,
 * is how TypeScript enforces it — add a member and this stops compiling, in the one function
 * every gate consults.
 *
 * Each cell of the table is a spend that must not happen:
 *
 * | | `full` | `gaps` | `subject` | `scoped` |
 * | --- | --- | --- | --- | --- |
 * | `research` | yes | yes | yes | no |
 * | `uploads` | no | no | no | no |
 * | `scoped` | **no** | **no** | yes | yes |
 *
 * `scoped` refusing `full` is the whole reason the value exists: `full` outlines from the
 * objective text and four web searches, which is exactly the syllabus-beside-the-material the
 * learner opted out of. It refuses `gaps` for the same reason one step removed — a gaps pass
 * asks the open web what the *topic* contains that the outline missed, so every subject it can
 * propose is by construction one the learner's own material does not cover.
 *
 * It allows `subject`, and that is not an oversight. "Go deeper on this one" is pointed at a
 * subject the learner's own chapter named, so researching it further is *filling in around my
 * material* rather than beside it. It is also the cheapest control in the system
 * (`SUBJECT_BUDGET_USD`), and withholding it would leave a scoped objective with no way at all
 * to ask for more about a chapter it already has.
 *
 * `research` refusing `scoped` is the mirror: the scoped outline reads the content store, and a
 * researched objective with an empty store would get an outline written out of nothing.
 */
export function allowsResearch(source, mode) {
    switch (source) {
        case 'research':
            return mode !== 'scoped';
        case 'uploads':
            return false;
        case 'scoped':
            return mode === 'scoped' || mode === 'subject';
    }
}
/**
 * Whether this objective's *first* plan is built out of the pages the learner adds.
 *
 * True for `uploads` for F-118's reason — there is nothing else — and true for `scoped` for a
 * reason worth saying out loud, because it looks at first like the opposite: a scoped objective
 * researches, so one might expect its first plan to wait for the research the way a `research`
 * objective's does. It must not, and two separate failures follow from making it.
 *
 * The first is a stall. A scoped objective's research is started *by* the upload settling, so a
 * pass that fails before it writes anything leaves nothing behind that can ever build a plan —
 * and the learner photographs page after page into silence, which is this project's signature
 * failure arriving from a fourth direction.
 *
 * The second is the product. *"Start from mine and fill in the gaps"* means the learner's own
 * chapter is a plan the moment it has been read, and the research layers into it afterwards.
 * Waiting would make this mode slower than the one it improves on.
 *
 * What stops a plan being built out of a photograph of a cover is the readiness gauge, which
 * measures the material. Not this.
 */
export function buildsFirstPlanFromUploads(source) {
    switch (source) {
        case 'research':
            return false;
        case 'uploads':
            return true;
        case 'scoped':
            return true;
    }
}
/**
 * Whether an upload settling should also set a research pass looking around it. F-119.
 *
 * Only `scoped`. A `research` objective has already had, or will be given, its own outline from
 * the objective text, and an upload on it is material to fold rather than a reason to research
 * again — F-51's two buttons are how its owner asks for that, deliberately. An `uploads`
 * objective reaches no research model at any price.
 */
export function researchesAroundUploads(source) {
    switch (source) {
        case 'research':
            return false;
        case 'uploads':
            return false;
        case 'scoped':
            return true;
    }
}
/**
 * Whether a node is in play, following its parent when it has one.
 *
 * Selection lives only on top-level nodes, so a nested topic's own `active` is
 * meaningless and is deliberately not consulted: the question "is this being
 * studied" has exactly one answer per subject, and storing it per leaf would let a
 * subtree disagree with the section heading the learner unticked.
 */
export function isActiveNode(node, byId) {
    // Bounded rather than recursive: a `parentId` cycle is a corrupt curriculum, and
    // the honest response is to treat the node as active and let it be seen, not to
    // hang the pass that reads it.
    let current = node;
    for (let depth = 0; depth < 32; depth++) {
        if (!current.parentId)
            return current.active !== false;
        const parent = byId.get(current.parentId);
        // An orphan is treated as its own root, matching how the outline renders it —
        // it is visible to the learner, so it must be answerable here too.
        if (!parent)
            return current.active !== false;
        current = parent;
    }
    return true;
}
//# sourceMappingURL=domain.js.map